import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, SlidersHorizontal, Loader2, RotateCcw } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORIGEM = { efic_min: 20, efic_max: 250, tempo_peca_pct: 20, qtd_min: 5 };

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(Number(v)));
const fmtDataHora = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Mesmas regras do backend (_validar_config_suspeitos)
const validar = (v) => {
  const n = {
    efic_min: v.efic_min === '' ? NaN : Number(v.efic_min),
    efic_max: v.efic_max === '' ? NaN : Number(v.efic_max),
    tempo_peca_pct: v.tempo_peca_pct === '' ? NaN : Number(v.tempo_peca_pct),
    qtd_min: v.qtd_min === '' ? NaN : Number(v.qtd_min),
  };
  if (Object.values(n).some((x) => Number.isNaN(x))) return { erro: 'Preencha todos os campos', n };
  if (n.efic_min < 0) return { erro: 'A eficiência mínima não pode ser negativa', n };
  if (n.efic_max <= n.efic_min) return { erro: 'A eficiência máxima tem de ser maior que a mínima', n };
  if (n.efic_max > 2000) return { erro: 'A eficiência máxima não pode passar de 2000%', n };
  if (n.tempo_peca_pct < 0 || n.tempo_peca_pct > 100) return { erro: 'O tempo por peça tem de estar entre 0% e 100% do padrão', n };
  if (n.qtd_min < 0 || !Number.isInteger(n.qtd_min)) return { erro: 'A quantidade mínima tem de ser um número inteiro ≥ 0', n };
  return { erro: null, n };
};

const DescricaoRegras = ({ c }) => (
  <>
    eficiência <strong>acima de {fmt(c.efic_max)}%</strong>, eficiência <strong>abaixo de {fmt(c.efic_min)}%</strong> (e maior que 0)
    {Number(c.tempo_peca_pct) > 0 ? (
      <>, ou tempo/peça <strong>abaixo de {fmt(c.tempo_peca_pct)}% do padrão</strong> (com quantidade &gt; {fmt(c.qtd_min)})</>
    ) : (
      <> (regra do tempo por peça <strong>desligada</strong>)</>
    )}
  </>
);

// Barra visual: zona suspeita (vermelho) · zona normal (verde) · zona suspeita (vermelho)
const BarraLimites = ({ min, max }) => {
  const escala = Math.max(300, (Number(max) || 0) * 1.2);
  const pMin = Math.min(100, Math.max(0, ((Number(min) || 0) / escala) * 100));
  const pMax = Math.min(100, Math.max(pMin, ((Number(max) || 0) / escala) * 100));
  return (
    <div className="space-y-1" data-testid="suspeitos-barra-limites">
      <div className="relative h-3 rounded-full overflow-hidden flex">
        <div className="bg-red-300" style={{ width: `${pMin}%` }} />
        <div className="bg-emerald-300" style={{ width: `${pMax - pMin}%` }} />
        <div className="bg-red-300 flex-1" />
      </div>
      <div className="relative h-4 text-[10px] text-stone-500">
        <span className="absolute left-0">0%</span>
        <span className="absolute -translate-x-1/2 font-semibold text-red-700" style={{ left: `${pMin}%` }}>{fmt(min)}%</span>
        <span className="absolute -translate-x-1/2 font-semibold text-red-700" style={{ left: `${pMax}%` }}>{fmt(max)}%</span>
      </div>
      <p className="text-[11px] text-stone-500"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-300 align-middle mr-1" />suspeito (aparece na lista e sai das médias) · <span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-300 align-middle mx-1" />normal</p>
    </div>
  );
};

export const LimitesSuspeitosCard = ({ onChanged }) => {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({ efic_min: '', efic_max: '', tempo_peca_pct: '', qtd_min: '' });
  const [preview, setPreview] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [saving, setSaving] = useState(false);
  const simReq = useRef(0);

  const carregarConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/relatorios/suspeitos/config`);
      setConfig(res.data);
    } catch {
      toast.error('Erro ao carregar os limites dos suspeitos');
    }
  }, []);

  useEffect(() => { carregarConfig(); }, [carregarConfig]);

  const abrir = () => {
    const c = config || ORIGEM;
    setVals({ efic_min: String(c.efic_min), efic_max: String(c.efic_max), tempo_peca_pct: String(c.tempo_peca_pct), qtd_min: String(c.qtd_min) });
    setPreview(null);
    setOpen(true);
  };

  const { erro, n } = validar(vals);

  // Pré-visualização (debounce) — quantos registos ficariam suspeitos com estes limites
  useEffect(() => {
    if (!open || erro) { setPreview(null); return undefined; }
    const id = ++simReq.current;
    setSimulando(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/relatorios/suspeitos/simular`, n);
        if (id === simReq.current) setPreview(res.data);
      } catch (e) {
        if (id === simReq.current) setPreview(null);
      } finally {
        if (id === simReq.current) setSimulando(false);
      }
    }, 450);
    return () => clearTimeout(t);
    // `n` deriva de `vals` (já nas dependências) — incluí-lo re-dispararia a cada render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vals.efic_min, vals.efic_max, vals.tempo_peca_pct, vals.qtd_min, erro]);

  const guardar = async () => {
    if (erro) { toast.error(erro); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/relatorios/suspeitos/config`, n, { timeout: 120000 });
      const r = res.data || {};
      toast.success(`Limites guardados. ${r.avaliados} registos reavaliados: ${r.marcados} passaram a suspeitos, ${r.desmarcados} deixaram de ser. Total: ${r.suspeitos}.`, { duration: 7000 });
      setOpen(false);
      await carregarConfig();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar os limites');
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setVals((v) => ({ ...v, [k]: e.target.value }));
  const c = config || ORIGEM;
  const alterado = config && (['efic_min', 'efic_max', 'tempo_peca_pct', 'qtd_min'].some((k) => Number(config[k]) !== Number(ORIGEM[k])));

  return (
    <>
      <Card className="p-4 bg-amber-50 border-amber-200" data-testid="suspeitos-limites-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-[280px]">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Registos suspeitos — revisão manual</p>
              <p className="text-xs" data-testid="suspeitos-regras-texto">
                Aparecem nesta lista os registos com <DescricaoRegras c={c} />.
                Estes registos são <strong>excluídos automaticamente</strong> das médias e rankings. Pode ainda <strong>excluir manualmente</strong> um registo para o remover do ranking (reversível).
              </p>
              {config && (
                <p className="text-[11px] text-amber-800/80 mt-1.5" data-testid="suspeitos-resumo">
                  {config.total_suspeitos} suspeito(s) · {config.total_excluidos_manual} excluído(s) manualmente
                  {alterado ? ' · limites personalizados' : ' · limites de origem'}
                  {config.atualizado_em ? ` · alterado em ${fmtDataHora(config.atualizado_em)}${config.atualizado_por ? ` por ${config.atualizado_por}` : ''}` : ''}
                </p>
              )}
            </div>
          </div>
          <Button size="sm" variant="outline" className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100" onClick={abrir} data-testid="suspeitos-definir-limites-btn">
            <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Definir limites
          </Button>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => !v && !saving && setOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="suspeitos-limites-dialog">
          <DialogHeader>
            <DialogTitle>Limites dos registos suspeitos</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">
            Defina o que é considerado <strong>suspeito</strong>. Os registos fora destes limites aparecem na lista de Suspeitos e deixam de contar para as médias e rankings.
            Ao guardar, <strong>todos os registos</strong> são reavaliados com os novos limites (as exclusões manuais mantêm-se).
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Eficiência mínima (%)</Label>
              <p className="text-[11px] text-stone-500 mb-1">Abaixo disto (e &gt; 0) → suspeito</p>
              <Input type="number" min="0" step="1" value={vals.efic_min} onChange={set('efic_min')} data-testid="suspeitos-efic-min-input" />
            </div>
            <div>
              <Label className="text-xs">Eficiência máxima (%)</Label>
              <p className="text-[11px] text-stone-500 mb-1">Acima disto → suspeito</p>
              <Input type="number" min="1" step="1" value={vals.efic_max} onChange={set('efic_max')} data-testid="suspeitos-efic-max-input" />
            </div>
          </div>

          {!erro && <BarraLimites min={n.efic_min} max={n.efic_max} />}

          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-stone-100">
            <div>
              <Label className="text-xs">Tempo por peça mínimo (% do padrão)</Label>
              <p className="text-[11px] text-stone-500 mb-1">Mais rápido que isto → suspeito · 0 = desligado</p>
              <Input type="number" min="0" max="100" step="1" value={vals.tempo_peca_pct} onChange={set('tempo_peca_pct')} data-testid="suspeitos-tempo-peca-input" />
            </div>
            <div>
              <Label className="text-xs">A partir de quantas peças</Label>
              <p className="text-[11px] text-stone-500 mb-1">Regra do tempo/peça só com qtd &gt; este valor</p>
              <Input type="number" min="0" step="1" value={vals.qtd_min} onChange={set('qtd_min')} disabled={Number(vals.tempo_peca_pct) === 0} data-testid="suspeitos-qtd-min-input" />
            </div>
          </div>

          {erro ? (
            <p className="text-xs text-red-600" data-testid="suspeitos-limites-erro">{erro}</p>
          ) : (
            <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700 min-h-[2.5rem] flex items-center gap-2" data-testid="suspeitos-preview">
              {simulando || !preview ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400" /> A calcular quantos registos ficariam suspeitos…</>
              ) : (
                <span>
                  Com estes limites: <strong className="text-navy" data-testid="suspeitos-preview-total">{preview.suspeitos}</strong> registo(s) suspeito(s) de {preview.avaliados}
                  {config ? <> (atualmente {config.total_suspeitos})</> : null}
                  {' · '}<span className="text-red-700">+{preview.marcados} novos</span>
                  {' · '}<span className="text-emerald-700">−{preview.desmarcados} deixam de ser</span>
                </span>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="ghost" size="sm" onClick={() => setVals({ efic_min: String(ORIGEM.efic_min), efic_max: String(ORIGEM.efic_max), tempo_peca_pct: String(ORIGEM.tempo_peca_pct), qtd_min: String(ORIGEM.qtd_min) })} disabled={saving} data-testid="suspeitos-repor-origem-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Repor valores de origem
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
              <Button type="button" onClick={guardar} disabled={saving || !!erro} data-testid="suspeitos-guardar-limites-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> A reavaliar…</> : 'Guardar e reavaliar'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LimitesSuspeitosCard;
